import { forwardRef, useState, Fragment } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { format, parseISO } from 'date-fns'

import useDates from 'services/hooks/useDate'
import { getInterviewSlot, listCalendar } from 'services/api/modules/recruitment/external-talent'
import TalentDayDiv from './TalentDayDiv'
import TalentCalendarDrawer from './TalentCalendarDrawer'
import CalendarDetailsModal from './CalendarDetailsModal'
import { useQuery } from 'react-query'

const dayofWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const TalentMonthlyCalendar = forwardRef(({ calendarValue }, ref) => {
  const { currentMonth } = useDates({ date: calendarValue })

  //drawer
  const [drawerState, setDrawerState] = useState({ right: false })
  const [dateData, setDateData] = useState(new Date())
  const handleDrawer = (anchor, open) => () => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  // modal
  const [modalState, setModalState] = useState(false)
  const [modalData, setModalData] = useState([])
  const handleModalOpen = () => {
    setModalState(true)
  }
  const handleModalClose = () => {
    setModalState(false)
  }
  const handleModalData = (data) => {
    setModalData(data)
  }
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const mobile_l = useMediaQuery(theme.breakpoints.up('mobile_l'))

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }

  const dateObject = new Date(currentMonth[0]?.fullDate)
  // Extract day, month, and year
  const day = dateObject.getDate() // Returns the day of the month (1-31)
  const month = dateObject.getMonth() + 1 // Returns the month (0-11); adding 1 to make it 1-12
  const year = dateObject.getFullYear() // Returns the year (four digits)

  const { data: allInterviewData, isLoading } = useQuery(
    ['listCalendar', year, month],
    async () => {
      if (year && month) {
        const { data } = await listCalendar(year, month)
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const { data: interviewSlotData, isLoading: interviewSlotIsLoading } = useQuery(
    ['getInterviewSlot', modalData],
    async () => {
      if (drawerState.right) {
        const { data } = await getInterviewSlot(modalData)
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const [...calendarEvent] = Object.entries(allInterviewData || {})

  return (
    <Box width={1} height={1}>
      <Box
        width={1}
        display="flex"
        flexWrap="wrap"
        sx={{
          overflow: 'hidden',
          position: 'sticky',
          top: '0',
          background: 'white',
          zIndex: '1',
          boxShadow: '1px 1px 2px rgb(0 0 0 / 0.10)',
          borderTop: '1px solid #E5E5E5',
          borderLeft: '1px solid #E5E5E5',
          borderRight: '1px solid #E5E5E5',
        }}
      >
        {dayofWeek.map((day) => (
          <Box
            key={day}
            sx={{
              width: 'calc(100% / 7)',
              height: '50px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body2"
              color="light-gray-text.main"
              sx={{ fontWeight: sm ? 'bold' : '' }}
            >
              {sm ? day : capitalizeFirstLetter(day)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        width={1}
        display="flex"
        flexWrap="wrap"
        sx={{
          borderRight: '1px solid #E5E5E5',
          overflow: 'auto',
        }}
        className="scrollbar"
      >
        {currentMonth.map((day, index) => {
          // This render the boxes before the 1st day of the month
          if (index === 0) {
            const offset = (day.hari - 1 + 7) % 7
            return (
              <Fragment key={index}>
                {Array.from({ length: offset }, (_, k) => (
                  <Box
                    key={k}
                    sx={{
                      width: 'calc(100% / 7)',
                      height: mobile_l ? '180px' : '140px',
                      borderLeft: '1px solid #E5E5E5',
                      borderTop: '1px solid #E5E5E5',
                      background: '#F6F6F6',
                    }}
                  />
                ))}
                <TalentDayDiv
                  day={day}
                  ref={ref}
                  index={index}
                  value={calendarValue}
                  calendarEvent={
                    calendarEvent.length > 0 &&
                    calendarEvent?.find(
                      (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                    )
                  }
                  setDrawerData={setDateData}
                  drawerState={drawerState}
                  setDrawerState={setDrawerState}
                  isLoading={isLoading}
                />
              </Fragment>
            )
          }

          // This render the boxes after the final day of the month
          if (index === currentMonth.length - 1) {
            return (
              <Fragment key={index}>
                <TalentDayDiv
                  day={day}
                  ref={ref}
                  index={index}
                  value={calendarValue}
                  calendarEvent={
                    calendarEvent.length > 0 &&
                    calendarEvent?.find(
                      (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                    )
                  }
                  setDrawerData={setDateData}
                  drawerState={drawerState}
                  setDrawerState={setDrawerState}
                  isLoading={isLoading}
                />
                {day.hari !== 0
                  ? Array.from({ length: 7 - day.hari }, (_, k) => (
                      <Box
                        key={k}
                        sx={{
                          width: 'calc(100% / 7)',
                          height: mobile_l ? '180px' : '140px',
                          borderLeft: '1px solid #E5E5E5',
                          borderTop: '1px solid #E5E5E5',
                          background: '#F6F6F6',
                        }}
                      />
                    ))
                  : null}
              </Fragment>
            )
          }
          // This render all the boxes except 1st and last day of the month
          return (
            <TalentDayDiv
              key={index}
              day={day}
              index={index}
              ref={ref}
              value={calendarValue}
              calendarEvent={
                calendarEvent.length > 0 &&
                calendarEvent?.find(
                  (item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate
                )
              }
              setDrawerData={setDateData}
              drawerState={drawerState}
              setDrawerState={setDrawerState}
              isLoading={isLoading}
              modalState={modalState}
              setModalState={setModalState}
            />
          )
        })}
      </Box>
      <TalentCalendarDrawer
        state={drawerState}
        handleDrawer={handleDrawer}
        dateData={dateData}
        setData={handleModalData}
        calendarEvent={calendarEvent}
        handleModal={handleModalOpen}
      />
      <CalendarDetailsModal
        open={modalState}
        handleClose={handleModalClose}
        data={interviewSlotData}
        dateData={dateData}
        interviewSlotIsLoading={interviewSlotIsLoading}
      />
    </Box>
  )
})

export default TalentMonthlyCalendar
